/**
 * Service Worker Cache Utility Functions
 * Handles cache invalidation and management from the app
 */

/**
 * Store cache policy in localStorage for Service Worker to read
 * Called whenever cache policy is updated in admin panel
 * @param {object} cachePolicy - Cache policy object with TTL settings
 */
export const updateServiceWorkerCachePolicy = async (cachePolicy) => {
  try {
    // Store in localStorage for SW to read
    localStorage.setItem('happyears-cache-policy', JSON.stringify(cachePolicy));
    
    // Send to active Service Worker to update in-memory policy
    if (navigator.serviceWorker?.controller) {
      return new Promise((resolve) => {
        const channel = new MessageChannel();
        
        channel.port1.onmessage = (event) => {
          console.log('[SWCacheUtils] Cache policy updated in SW:', event.data);
          resolve(true);
        };

        navigator.serviceWorker.controller.postMessage(
          {
            type: 'UPDATE_CACHE_POLICY',
            cachePolicy,
          },
          [channel.port2]
        );

        // Timeout fallback
        setTimeout(() => resolve(true), 2000);
      });
    }
    return true;
  } catch (error) {
    console.error('[SWCacheUtils] Failed to update cache policy:', error);
    return false;
  }
};

/**
 * Get current cache policy from localStorage
 * @returns {object} Cache policy object
 */
export const getServiceWorkerCachePolicy = () => {
  try {
    const stored = localStorage.getItem('happyears-cache-policy');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('[SWCacheUtils] Failed to get cache policy:', error);
    return null;
  }
};

/**
 * Invalidate specific API endpoints from cache
 * @param {string|string[]} endpoints - Single endpoint or array of endpoints to invalidate
 * @returns {Promise<boolean>} Success status
 */
export const invalidateApiCache = async (endpoints) => {
  if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
    console.warn('Service Worker not available');
    return false;
  }

  return new Promise((resolve) => {
    const channel = new MessageChannel();
    
    channel.port1.onmessage = (event) => {
      if (event.data.success) {
        console.log('Cache invalidation successful');
        resolve(true);
      }
    };

    navigator.serviceWorker.controller.postMessage(
      {
        type: 'INVALIDATE_CACHE',
        endpoints: Array.isArray(endpoints) ? endpoints : [endpoints],
      },
      [channel.port2]
    );

    // Timeout fallback
    setTimeout(() => resolve(false), 5000);
  });
};

/**
 * Invalidate cache for a specific resource type
 * Maps resource types to actual backend API endpoints
 * @param {string} resourceType - 'patients', 'services', 'products', 'invoices', 'doctors', etc.
 * @returns {Promise<boolean>}
 */
export const invalidateCacheByType = async (resourceType) => {
  // Map resource types to their actual backend endpoints
  const endpointMap = {
    patient: ['get-patient-list-paged', 'get-patients-brief-by-ids', 'search-patients-brief'],
    product: ['get-product-list-paged', 'search-product-brief'],
    service: ['get-service-list', 'get-service-list-paged'],
    invoice: ['get-invoice-list-paged', 'get-invoices-for-month-report'],
    doctor: ['get-doctor-list'],
    branch: ['get-branch-list'],
    salesperson: ['get-salesperson-list'],
    audiometry: ['get-audiometry-list-paged'],
    user: ['get-user-list'],
    cachePolicySettings: ['get-cache-policy-settings'],
  };

  const endpoints = endpointMap[resourceType] || [];
  if (endpoints.length === 0) {
    console.warn(`No endpoints mapped for resource type: ${resourceType}`);
    return false;
  }

  return invalidateApiCache(endpoints);
};

/**
 * Invalidate all patient-related cache
 * @returns {Promise<boolean>}
 */
export const invalidatePatientCache = () => invalidateCacheByType('patient');

/**
 * Invalidate all service-related cache
 * @returns {Promise<boolean>}
 */
export const invalidateServiceCache = () => invalidateCacheByType('service');

/**
 * Invalidate all product-related cache
 * @returns {Promise<boolean>}
 */
export const invalidateProductCache = () => invalidateCacheByType('product');

/**
 * Invalidate all invoice-related cache
 * @returns {Promise<boolean>}
 */
export const invalidateInvoiceCache = () => invalidateCacheByType('invoice');

/**
 * Invalidate all doctor-related cache
 * @returns {Promise<boolean>}
 */
export const invalidateDoctorCache = () => invalidateCacheByType('doctor');

/**
 * Invalidate all branch-related cache
 * @returns {Promise<boolean>}
 */
export const invalidateBranchCache = () => invalidateCacheByType('branch');

/**
 * Invalidate all salesperson-related cache
 * @returns {Promise<boolean>}
 */
export const invalidateSalespersonCache = () => invalidateCacheByType('salesperson');

/**
 * Invalidate all audiometry-related cache
 * @returns {Promise<boolean>}
 */
export const invalidateAudiometryCache = () => invalidateCacheByType('audiometry');

/**
 * Trigger service worker update check
 * @returns {Promise<void>}
 */
export const checkForServiceWorkerUpdate = async () => {
  if (!navigator.serviceWorker) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.update();
    console.log('Service Worker update check completed');
  } catch (error) {
    console.error('Service Worker update check failed:', error);
  }
};

/**
 * Manually activate waiting service worker (for new updates)
 * @returns {void}
 */
export const activateNewServiceWorker = () => {
  if (!navigator.serviceWorker || !navigator.serviceWorker.controller) return;

  navigator.serviceWorker.controller.postMessage({
    type: 'SKIP_WAITING',
  });

  // Listen for controller change
  let refreshing = false;
  navigator.serviceWorker.oncontrollerchange = () => {
    if (!refreshing) {
      refreshing = true;
      window.location.reload();
    }
  };
};

/**
 * Force refresh data by invalidating cache and reloading
 * @param {string} resourceType - Resource type to invalidate
 * @returns {Promise<void>}
 */
export const forceRefreshData = async (resourceType) => {
  await invalidateCacheByType(resourceType);
  // Let the component re-fetch from network
  window.dispatchEvent(new CustomEvent('cache-invalidated', { detail: { resourceType } }));
};

/**
 * Get current service worker controller status
 * @returns {object} Status object
 */
export const getServiceWorkerStatus = () => {
  const controller = navigator.serviceWorker?.controller;
  const registration = navigator.serviceWorker?.controller?.registration;

  return {
    isControlled: !!controller,
    controllerScriptURL: controller?.scriptURL,
    hasWaitingWorker: !!registration?.waiting,
    hasNewWorker: !!registration?.installing,
    isReady: !!navigator.serviceWorker?.ready,
  };
};
